import * as React from 'react'

import { ExternalLink } from 'lucide-react'
import { MobXProviderContext } from 'mobx-react'

import { ITypeFieldFilterOptions, OperatorType } from '~/client/graph'
import { EquipmentTab } from '~/client/src/desktop/views/ProjectSetUp/components/Equipment/EquipmentSetupTabs'
import { LocationsTab } from '~/client/src/desktop/views/ProjectSetUp/components/Locations/LocationsSetupTabs'
import { MaterialResourcesTab } from '~/client/src/desktop/views/ProjectSetUp/components/MaterialResources/MaterialResourcesSetupTabs'
import { FormTypeEnabledResourcesContext } from '~/client/src/desktop/views/ProjectSetUp/components/PermitsSetUp/components/FormTypeConfigurator/FormTypeEnabledResourcesContext'
import ProjectSetUpPageStore from '~/client/src/desktop/views/ProjectSetUp/ProjectSetUpPage.store'
import ProjectSetUpPage, {
  SETUP_TAB_QUERY_KEY,
} from '~/client/src/desktop/views/ProjectSetUp/ProjectSetUpPages'
import {
  Dialog,
  DialogContent,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import { locationFilterPropMap } from '~/client/src/shared/constants/FilterPropertyMaps/LocationFilterPropMap'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import {
  useResourceCardSchema,
  type ResourceCardSchema,
} from '~/client/src/shared/hooks/useResourceCardSchema'
import { useResourceControls } from '~/client/src/shared/hooks/useResourceControls'
import { useResourceRuntimeContext } from '~/client/src/shared/hooks/useResourceRuntimeContext'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type { IResourceControlCommands } from '~/client/src/shared/resources/domain/resourceControls'
import { renderResourceRecordIcon } from '~/client/src/shared/resources/icons/resourceRecordIcon'
import { renderResourceSemanticIcon } from '~/client/src/shared/resources/icons/resourceSemanticIcons'
import { getResourceIconKey } from '~/client/src/shared/resources/icons/resourceTypeIcons'
import { toast } from '~/client/src/shared/utils/toast'

import { renderRestrictionChip } from '../WorkflowFieldRestrictionChip'
import WorkflowFieldRestrictionChipArea from '../WorkflowFieldRestrictionChipArea'
import {
  WorkflowFieldRestrictionContext,
  WorkflowFieldRestrictionItem,
} from '../WorkflowFieldRestrictionContext'
import WorkflowFieldRestrictionPanel from '../WorkflowFieldRestrictionPanel'
import ResourceSelectionContent from './ResourceSelectionContent'

interface IProps {
  schema: ResourceCardSchema
  resourceTypeId: string
  resourceLabel: string
  records: IResourceRecord[]
  fieldFilterOptions: ITypeFieldFilterOptions[]
  onChange: (updatedFilters: ITypeFieldFilterOptions[]) => void
  recordPredicate?: (record: IResourceRecord) => boolean
  projectId: string
  /** Lets the "add more" flow turn the WR toggle on for the resources it enables. */
  controlCommands: IResourceControlCommands
}

// resourceLabel is unsuitable for the copy (materials resolve to "material
// resource"), so pass the proper capitalized noun per type. null = no link.
function getMoreResourcesConfig(resourceTypeId: string): {
  page: ProjectSetUpPage
  tab: string
  /** The resource's own plural name, which every string here is built from. */
  resource: string
} | null {
  switch (resourceTypeId) {
    case ResourceTypeId.OFFLOADING_EQUIPMENT:
      return {
        page: ProjectSetUpPage.EQUIPMENT,
        tab: EquipmentTab.Controls,
        resource: Localization.translator.equipment,
      }
    case ResourceTypeId.MATERIALS:
      return {
        page: ProjectSetUpPage.MATERIALS,
        tab: MaterialResourcesTab.Controls,
        resource: Localization.translator.materials,
      }
    case ResourceTypeId.LOCATIONS:
      return {
        page: ProjectSetUpPage.LOCATIONS,
        tab: LocationsTab.Controls,
        resource: Localization.translator.locations,
      }
    default:
      return null
  }
}

function getStaticIdValues(
  filterOptions: ITypeFieldFilterOptions[],
  operator: OperatorType,
): Set<string> {
  const filter = filterOptions?.find(
    f =>
      f.propertyPath === locationFilterPropMap.ID &&
      f.operator === operator &&
      !f.fieldId,
  )
  return new Set(filter?.values ?? [])
}

// Stable identity: the selector re-seeds its draft whenever selectedIds changes identity,
// and this one opens empty every time.
const EMPTY_IDS: string[] = []

const isNotBookable = (record: IResourceRecord) => !record.root?.isBookable

const ResourceRestriction: React.FC<IProps> = ({
  schema,
  resourceTypeId,
  resourceLabel,
  records,
  fieldFilterOptions,
  onChange,
  recordPredicate,
  projectId,
  controlCommands,
}) => {
  const runtimeContext = useResourceRuntimeContext()
  const {
    cardConfiguration,
    properties,
    propertiesReady,
    ready,
    getDisplayValue,
  } = useResourceCardSchema({ schema, resourceTypeId, runtimeContext })

  const chipTypeIcon = React.useMemo(() => {
    const iconKey = getResourceIconKey(resourceTypeId)
    return iconKey ? renderResourceSemanticIcon(iconKey) : undefined
  }, [resourceTypeId])

  const recordsById = React.useMemo(
    () => new Map(records.map(record => [record.id, record])),
    [records],
  )

  // A chip names one resource, so equipment shows that record's own glyph rather
  // than the type's — the same icon its lists and the selector show.
  const renderChip = React.useCallback(
    (item: WorkflowFieldRestrictionItem): JSX.Element => {
      const icon = renderResourceRecordIcon(
        resourceTypeId,
        recordsById.get(item.key),
        chipTypeIcon,
        'tw:block tw:size-full',
      )

      return renderRestrictionChip(
        item,
        icon ? (
          <span className="tw:flex-none tw:ml-0.5 tw:mr-[5px] tw:size-4">
            {icon}
          </span>
        ) : null,
      )
    },
    [resourceTypeId, recordsById, chipTypeIcon],
  )

  // Ids of the currently-selectable resources (the picker hides non-bookable
  // records via recordPredicate).
  const selectableIds = React.useMemo(
    () =>
      (recordPredicate ? records.filter(recordPredicate) : records).map(
        r => r.id,
      ),
    [records, recordPredicate],
  )

  const allowedIds = React.useMemo(
    () => getStaticIdValues(fieldFilterOptions, OperatorType.In),
    [fieldFilterOptions],
  )

  // The Workflow Types control revokes a single resource by writing a NotIn entry - it
  // knows one record, not the whole universe, so it cannot express the revocation as a
  // positive list. Read it here or this panel shows a denied resource as still allowed.
  const deniedIds = React.useMemo(
    () => getStaticIdValues(fieldFilterOptions, OperatorType.NotIn),
    [fieldFilterOptions],
  )

  // What the field actually permits right now: an empty allow-list means every selectable
  // resource, and the deny-list subtracts from whichever base applies. This drives the
  // picker's ticks AND the chips, so the two can never disagree.
  const selectedIds = React.useMemo(() => {
    const base = allowedIds.size ? Array.from(allowedIds) : selectableIds
    return new Set(
      deniedIds.size ? base.filter(id => !deniedIds.has(id)) : base,
    )
  }, [allowedIds, deniedIds, selectableIds])

  // "Any" only when nothing narrows the field at all - a deny-list narrows it.
  const isAllSelected = allowedIds.size === 0 && deniedIds.size === 0

  const [isOpen, setIsOpen] = React.useState(false)

  const { projectSetUpPageStore } = React.useContext(MobXProviderContext) as {
    projectSetUpPageStore?: ProjectSetUpPageStore
  }
  const moreResources = getMoreResourcesConfig(resourceTypeId)

  const handleGoToResourceControls = () => {
    if (!moreResources || !projectSetUpPageStore) return
    projectSetUpPageStore.navigateTo(moreResources.page, {
      [SETUP_TAB_QUERY_KEY]: moreResources.tab,
    })
  }

  const handleApply = React.useCallback(
    (ids: string[]) => {
      // "Allow all" = every selectable resource is selected → clear to the empty
      // (Any) state so resources added later, or re-enabled as bookable, stay
      // allowed. Test set membership, not lengths: the saved allow-list (hence
      // `ids`) can retain stale ids (resources now hidden by the predicate), which
      // would otherwise both miss the collapse and, by coincidence of counts,
      // trigger a false collapse.
      const idsSet = new Set(ids)
      const allSelectableSelected =
        selectableIds.length > 0 && selectableIds.every(id => idsSet.has(id))
      if (ids.length === 0 || allSelectableSelected) {
        onChange([])
        return
      }
      onChange([
        {
          propertyPath: locationFilterPropMap.ID,
          operator: OperatorType.In,
          values: ids,
        },
      ])
    },
    [onChange, selectableIds],
  )

  const handleRemoveItem = React.useCallback(
    (key: string) => {
      const newIds = Array.from(selectedIds).filter(id => id !== key)
      handleApply(newIds)
    },
    [selectedIds, handleApply],
  )

  // The resources this field cannot offer yet: WR is off, so nothing can book them. They
  // are the entire point of the "add more" flow, and the only thing it lists.
  const [isEnableSelectorOpen, setIsEnableSelectorOpen] = React.useState(false)
  const [isEnabling, setIsEnabling] = React.useState(false)
  const { saveBookable } = useResourceControls(projectId, controlCommands)
  const draft = React.useContext(FormTypeEnabledResourcesContext)

  const handleEnableAndAllow = React.useCallback(
    async (ids: string[]) => {
      if (!ids.length) {
        setIsEnableSelectorOpen(false)
        return
      }

      setIsEnabling(true)
      try {
        // Enabling is a resource-level write that lands immediately, exactly as the WR
        // toggle in the controls table does; the allow-list edit below is part of the
        // permit-type draft and lands with the form's Save.
        await saveBookable(ids, true)
        // Union with what the field allows TODAY, through handleApply so its collapse
        // rule still applies: a field showing "Any" stays empty (an empty list already
        // allows the newly enabled resources, and naming them would needlessly freeze out
        // everything added later), while a field holding a specific list gains them in
        // that list. The effective set already omits denied records, so their exclusion
        // survives as omission.
        handleApply(Array.from(new Set([...selectedIds, ...ids])))
        // After this field's own write, which replaces its deny-list with the allow-list
        // above: enabling denied the resources on every other restricted field of the type,
        // and the draft has to carry that or its save drops it.
        draft?.denyEnabledResources(resourceTypeId, ids)
        setIsEnableSelectorOpen(false)
      } catch {
        toast.error(Localization.translator.failedToSaveChangesTryAgain)
      } finally {
        setIsEnabling(false)
      }
    },
    [saveBookable, handleApply, selectedIds, draft, resourceTypeId],
  )

  const chipItems = React.useMemo<WorkflowFieldRestrictionItem[]>(() => {
    if (isAllSelected) return []
    // Hide chips for allowed resources that now fail the rule (e.g. WR turned
    // off / excluded type), matching the picker list. The id stays in the saved
    // allow-list (harmless — runtime hides it too) and the chip reappears if the
    // resource becomes valid again.
    return records
      .filter(
        r => selectedIds.has(r.id) && (!recordPredicate || recordPredicate(r)),
      )
      .map(r => ({
        key: r.id,
        label: r.name,
        isDeleted: r.isDeleted,
      }))
  }, [records, selectedIds, isAllSelected, recordPredicate])

  const ctxValue = React.useMemo(
    () => ({
      state: { items: chipItems, isAllSelected },
      actions: { remove: handleRemoveItem },
      meta: {
        emptyLabel: Localization.translator.any + ' ' + resourceLabel,
      },
    }),
    [chipItems, isAllSelected, handleRemoveItem, resourceLabel],
  )

  return (
    <WorkflowFieldRestrictionContext.Provider value={ctxValue}>
      <WorkflowFieldRestrictionPanel
        trigger={
          <WorkflowFieldRestrictionChipArea
            renderItem={renderChip}
            isOpen={isOpen}
          />
        }
        open={isOpen}
        onOpenChange={setIsOpen}
        side="bottom"
        align="start"
        sideOffset={4}
      >
        <ResourceSelectionContent
          resourceTypeId={resourceTypeId}
          resourceLabel={resourceLabel}
          records={records}
          // Unrestricted opens with every selectable resource ticked, so the user edits
          // DOWN from all: seeding from the empty allow-list showed nothing ticked under
          // an "Any" trigger, two opposite claims about the same field. Applying a full
          // selection collapses back to [], so re-saving an untouched "Any" stays "Any";
          // applying anything less rewrites it as a positive list, which is how a NotIn
          // written elsewhere gets absorbed into this panel's own model.
          selectedIds={Array.from(selectedIds)}
          cardConfiguration={cardConfiguration}
          properties={properties}
          propertiesReady={propertiesReady}
          loading={!ready}
          getDisplayValue={getDisplayValue}
          recordPredicate={recordPredicate}
          onApply={ids => {
            handleApply(ids)
            setIsOpen(false)
          }}
          onClose={() => setIsOpen(false)}
          footerNote={
            moreResources && (
              // Opens the enable-and-allow selector straight away. The confirmation step
              // that used to sit between them explained a rule this line now states, and
              // its only other action - going to the Controls tab - is offered inside the
              // selector instead, where it no longer costs the draft to read about.
              <>
                {Localization.translator.cantSeeResource(
                  moreResources.resource,
                )}{' '}
                <button
                  type="button"
                  className="tw:cursor-pointer tw:border-none tw:bg-transparent tw:p-0 tw:text-body-sm tw:text-primary tw:underline"
                  onClick={() => {
                    setIsOpen(false)
                    setIsEnableSelectorOpen(true)
                  }}
                >
                  {Localization.translator.openNonWorkflowResources(
                    moreResources.resource,
                  )}
                </button>
              </>
            )
          }
        />
      </WorkflowFieldRestrictionPanel>
      {moreResources && isEnableSelectorOpen && (
        <Dialog
          open
          onOpenChange={open => !open && setIsEnableSelectorOpen(false)}
        >
          <DialogContent
            // The selector brings its own header and close, so the dialog must not add a
            // second X. It also sizes itself (a fixed width and 80vh), so the dialog hugs
            // it rather than imposing a box: w-auto beats the base's w-full, and the
            // max-width overrides exist because the base caps at sm:max-w-sm, which is
            // narrower than the content and was clipping the toolbar.
            showCloseButton={false}
            className="tw:flex tw:w-auto tw:max-w-[calc(100vw-2rem)] tw:flex-col tw:overflow-hidden tw:p-0 tw:sm:max-w-none"
          >
            <ResourceSelectionContent
              resourceTypeId={resourceTypeId}
              resourceLabel={resourceLabel}
              // Every record, so in the LBS tree a WR-off location stays under its WR-on parent.
              records={records}
              recordPredicate={isNotBookable}
              selectedIds={EMPTY_IDS}
              cardConfiguration={cardConfiguration}
              properties={properties}
              propertiesReady={propertiesReady}
              loading={!ready || isEnabling}
              getDisplayValue={getDisplayValue}
              // Says what Save will do, which is not obvious: it does not merely pick from
              // a list, it turns the WR toggle on for what is picked.
              footerNote={
                <>
                  {Localization.translator.enablingResourcesHint(
                    moreResources.resource,
                  )}{' '}
                  {Localization.translator.alsoManageFromControls}{' '}
                  {/* The one path that still navigates, and so the one that still costs
                      unsaved work - carried here from the dialog this replaced. */}
                  {/* `inline`, not `inline-flex`: a flex box takes its baseline from its
                      first item, which here is the icon, so the whole link rode above the
                      sentence. As an inline run the text baseline is the button's, and the
                      icon aligns to it on its own. */}
                  <button
                    type="button"
                    className="tw:inline tw:cursor-pointer tw:border-none tw:bg-transparent tw:p-0 tw:align-baseline tw:text-body-sm tw:text-primary tw:underline"
                    onClick={handleGoToResourceControls}
                  >
                    <ExternalLink className="tw:mr-1 tw:inline-block tw:size-3.5 tw:align-text-bottom" />
                    {Localization.translator.resourceControlsPage(
                      moreResources.resource,
                    )}
                  </button>
                  .
                </>
              }
              onApply={handleEnableAndAllow}
              onClose={() => setIsEnableSelectorOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </WorkflowFieldRestrictionContext.Provider>
  )
}

export default ResourceRestriction
